import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@htx/environment';
import { MockDataSwitcherComponent } from './components/organisms/mock-data-switcher/mock-data-switcher.component';
import { MockService } from './mocks/mock.service';
import { NavigationService } from './services/navigation.service';
import { AuthService } from './services/auth/auth.service';
import {
  ButtonComponent,
  DropdownItem,
  HeaderComponent,
  ScrollToTopComponent
} from 'htx-ui-library';
import { FooterComponent } from './components/organisms/footer/footer.component';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    AsyncPipe,
    TranslocoPipe,
    MockDataSwitcherComponent,
    HeaderComponent,
    ButtonComponent,
    ScrollToTopComponent,
    FooterComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  protected readonly useMocks = environment.useMocks;

  private router = inject(Router);
  private authService = inject(AuthService);
  private navigationService = inject(NavigationService);
  private mockService = inject(MockService);
  private transloco = inject(TranslocoService);

  readonly availableLangs = ['de', 'en'];
  readonly activeLang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang()
  });

  setLang(lang: string): void {
    this.transloco.setActiveLang(lang);
  }

  // Authentication
  isLoggedIn$ = this.authService.getIsLoggedIn$();
  isAdmin$ = this.authService.getIsAdmin$();
  displayName$ = this.authService.displayName$;

  // Routes
  navigationRoutes$ = this.navigationService.getNavigationRoutes$();
  adminMenuRoutes$ = this.navigationService.getAdminMenuRoutes$();
  profileMenuRoutes$ = this.navigationService.getProfileMenuRoutes$();

  /**
   * Initializes the component.
   * Sets up navigation routes and initializes authentication based on the environment configuration.
   */
  ngOnInit() {
    // Initialize MirageJS if mocks are enabled
    if (environment.useMocks) {
      this.mockService.mirageJsServer();
    }
    this.authService.init();
  }

  /**
   * Cleans up resources when the component is destroyed.
   * Unsubscribes from authentication and mock services to prevent memory leaks.
   */
  ngOnDestroy() {}

  /**
   * Triggers the login process for the user.
   * Delegates to the AuthService to handle authentication flow.
   */
  login() {
    this.authService.login();
  }

  /**
   * Logs the user out of the application.
   * Delegates to the AuthService to handle logout and cleanup.
   */
  logout() {
    this.authService.logout();
  }

  logoClick() {
    this.router.navigate(['/']);
  }

  /**
   * Called when a navigation item is selected from the mobile menu.
   * Calls the onItemClick callback if available.
   *
   * @param item The selected NavigationItem from the header menu.
   */
  onNavigationItemClicked(item: any): void {
    if (item?.onItemClick) {
      item.onItemClick();
    }
  }

  /**
   * Called when an admin menu item is selected.
   * Navigates to the route specified in the DropdownItem, if available.
   * Also calls the onItemClick callback if it exists on the route.
   *
   * @param item The selected DropdownItem from the admin menu. Expects item.value to contain a navigation route.
   */
  onAdminMenuItemSelected(item: DropdownItem) {
    const route = item as any;
    if (route.onItemClick) {
      route.onItemClick();
      return;
    }
    if (item.value) {
      this.router.navigate([item.value]);
    }
  }
}
